import { useTranslate } from '@tolgee/react';
import ClipboardJS from 'clipboard';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import useInvitation from '../../../hooks/useInvitation';
import { useMetrics } from '../../../hooks/useMetrics';
import { InvitationRole } from '../../../services/backendService/types';
import { Input } from '../../_atoms/_Input';
import { Button } from '../../_atoms/Button';
import {
  BtnGhost,
  BtnOrange,
  C,
  FieldInput,
} from '../../dynamicPages/OrganizationSettingsPage/orgSettingsUi';

export const InvitationInputWithButton = ({
  role,
  isSignup,
  isFetching,
}: {
  role: InvitationRole;
  isSignup?: boolean;
  isFetching?: boolean;
}) => {
  const ref = useRef<ClipboardJS | undefined>();
  const { url } = useInvitation(role);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    ref.current = new ClipboardJS('#copy_url');
    const clip = ref.current;

    clip.on('success', () => {
      setCopySuccess(true);
    });

    return () => {
      clip.destroy();
    };
  }, []);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copySuccess) {
        setCopySuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copySuccess]);

  const validUrl = !isSignup ? url : `${window.origin}/signup`;

  return (
    <Container id="copy_url" data-clipboard-text={validUrl}>
      <Input style={{ flex: '1' }} fluid value={validUrl} />
      <Button
        type="ghost"
        disabled={isFetching}
        loading={isFetching}
        styles={{ marginLeft: 10, color: theme.colors.black }}
      >
        {copySuccess ? 'Copied!' : 'Copy'}
      </Button>
    </Container>
  );
};

export const InvitationButton = ({
  isSignup,
  url,
  isFetching,
}: {
  isSignup?: boolean;
  url?: string;
  isFetching?: boolean;
}) => {
  const ref = useRef<ClipboardJS | undefined>();
  const [copySuccess, setCopySuccess] = useState(false);
  const metrics = useMetrics();
  useEffect(() => {
    ref.current = new ClipboardJS('#copy_url');
    const clip = ref.current;

    clip.on('success', () => {
      setCopySuccess(true);
      metrics.logEvent('Invite.Copy.Link.Success');
    });

    return () => {
      clip.destroy();
    };
  }, [metrics]);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copySuccess) {
        setCopySuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copySuccess]);

  const validUrl = !isSignup ? url : `${window.origin}/signup`;
  const { t } = useTranslate();
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', width: '100%', marginLeft: '10px' }}
      id="copy_url"
      data-clipboard-text={validUrl}
    >
      <LinkField value={url} readOnly />
      {/* Ghost while idle — it sits beside the field as the secondary action — and
          filled orange on success, which is the app's own way of confirming. */}
      {copySuccess ? (
        <CopyBtnOrange type="button" disabled={isFetching}>
          {t('Copied!')}
        </CopyBtnOrange>
      ) : (
        <CopyBtnGhost type="button" disabled={isFetching}>
          {t('Copy')}
        </CopyBtnGhost>
      )}
    </div>
  );
};

const InvitationLink = ({
  role,
  isSignup,
}: {
  role: InvitationRole;
  isSignup?: boolean;
}) => {
  return (
    <div>
      <p>Invite a {role}</p>
      <InvitationInputWithButton role={role} isSignup={isSignup} />
    </div>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export default InvitationLink;

// The invite link itself: FieldInput's look, read-only, and monospace-ish room for
// a long URL. Flexes so the Copy button keeps its width on narrow screens.
const LinkField = styled(FieldInput)`
  flex: 1;
  min-width: 0;
  background: #fff;
  color: ${C.fg2};
`;
const CopyBtnGhost = styled(BtnGhost)`
  margin-left: 10px;
  flex-shrink: 0;
`;
const CopyBtnOrange = styled(BtnOrange)`
  margin-left: 10px;
  flex-shrink: 0;
`;
